import { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Badge,
  Fade,
  ClickAwayListener,
  Popper,
  Divider,
  Paper,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import { pulse, slideInItem } from "./animations";
import socket from "../../../utils/socket";

// Get dot color based on notification type
const getDotColor = (type) => {
  switch (type) {
    case "sla_breach":
      return "#ef4444";
    case "new_lead":
    case "lead_assigned":
      return "#22c55e";
    case "payment":
      return "#3b82f6";
    case "status_change":
      return "#f59e0b";
    case "booking":
      return "#8b5cf6";
    case "task":
      return "#06b6d4";
    case "followup_due":
      return "#f97316";
    default:
      return "#6b7280";
  }
};

// Format relative time from a date string
const timeAgo = (dateStr) => {
  const now = Date.now();
  const diff = now - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min${mins > 1 ? "s" : ""} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs > 1 ? "s" : ""} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days > 1 ? "s" : ""} ago`;
};

const NotificationDropdown = () => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const open = Boolean(anchorEl);
  const loadedRef = useRef(false);

  const unreadCount = notifications.filter((n) => !n.read).length;

  // Connect socket and fetch notifications
  useEffect(() => {
    const connectAndFetch = () => {
      if (!loadedRef.current) {
        socket.emit("notifications:get");
        loadedRef.current = true;
      }
    };

    if (socket.connected) {
      connectAndFetch();
    } else {
      socket.connect();
      socket.once("connect", connectAndFetch);
    }

    // Receive initial list
    const handleList = (list) => {
      setNotifications(list);
    };

    // Receive new real-time notification
    const handleNew = (notification) => {
      setNotifications((prev) => [notification, ...prev].slice(0, 50));
    };

    // All marked as read
    const handleAllRead = () => {
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    };

    // Single marked as read
    const handleUpdated = (id) => {
      setNotifications((prev) =>
        prev.map((n) => (n._id === id ? { ...n, read: true } : n))
      );
    };

    socket.on("notifications:list", handleList);
    socket.on("notification:new", handleNew);
    socket.on("notifications:all-read", handleAllRead);
    socket.on("notifications:updated", handleUpdated);

    return () => {
      socket.off("notifications:list", handleList);
      socket.off("notification:new", handleNew);
      socket.off("notifications:all-read", handleAllRead);
      socket.off("notifications:updated", handleUpdated);
    };
  }, []);

  const handleClick = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    socket.emit("notifications:read-all");
  }, []);

  const handleNotificationClick = useCallback((id) => {
    setNotifications((prev) =>
      prev.map((n) => (n._id === id ? { ...n, read: true } : n))
    );
    socket.emit("notifications:read", id);
  }, []);

  const handleViewAll = () => {
    handleClose();
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box>
        <IconButton
          onClick={handleClick}
          sx={{
            backgroundColor: "transparent",
            borderRadius: 2,
            p: 0.75,
            position: "relative",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "grey.100",
              transform: "scale(1.05)",
            },
          }}
        >
          <Badge
            variant="dot"
            overlap="circular"
            sx={{
              "& .MuiBadge-badge": {
                backgroundColor: "#ef4444",
                width: 10,
                height: 10,
                borderRadius: "50%",
                top: 2,
                right: 2,
                border: "2px solid #fff",
                animation: unreadCount > 0 ? `${pulse} 2s infinite` : "none",
              },
            }}
            invisible={unreadCount === 0}
          >
            <NotificationsNoneOutlinedIcon
              sx={{ fontSize: 22, color: "text.secondary" }}
            />
          </Badge>
        </IconButton>

        <Popper
          open={open}
          anchorEl={anchorEl}
          placement="bottom-end"
          transition
          sx={{ zIndex: 1300 }}
          modifiers={[
            {
              name: "offset",
              options: {
                offset: [0, 8],
              },
            },
          ]}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={250}>
              <Paper
                elevation={8}
                sx={{
                  width: 380,
                  maxWidth: "calc(100vw - 32px)",
                  borderRadius: "16px",
                  overflow: "hidden",
                  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15)",
                  border: "1px solid rgba(0, 0, 0, 0.05)",
                }}
              >
                {/* Header */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 3,
                    py: 2.5,
                    borderBottom: "1px solid #f1f5f9",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "18px",
                      fontWeight: 700,
                      color: "#1e293b",
                    }}
                  >
                    Notifications
                    {unreadCount > 0 && (
                      <Box
                        component="span"
                        sx={{
                          ml: 1,
                          fontSize: "12px",
                          fontWeight: 700,
                          color: "#fff",
                          bgcolor: theme.palette.primary.main,
                          px: 1,
                          py: 0.25,
                          borderRadius: "10px",
                          verticalAlign: "middle",
                        }}
                      >
                        {unreadCount}
                      </Box>
                    )}
                  </Typography>
                  <Button
                    onClick={handleMarkAllRead}
                    disableRipple
                    disabled={unreadCount === 0}
                    sx={{
                      textTransform: "none",
                      fontSize: "14px",
                      fontWeight: 500,
                      color: theme.palette.primary.main,
                      padding: 0,
                      minWidth: "auto",
                      "&:hover": {
                        backgroundColor: "transparent",
                        color: theme.palette.primary.dark,
                      },
                    }}
                  >
                    Mark all as read
                  </Button>
                </Box>

                {/* Notifications List */}
                <Box
                  sx={{
                    maxHeight: 380,
                    overflowY: "auto",
                    "&::-webkit-scrollbar": {
                      width: "6px",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "#fff",
                      borderRadius: "3px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: theme.palette.primary.main,
                      borderRadius: "3px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      backgroundColor: theme.palette.primary.dark,
                    },
                    scrollbarWidth: "thin",
                    scrollbarColor: `${theme.palette.primary.main} #fff`,
                  }}
                >
                  {notifications.length === 0 ? (
                    <Box sx={{ py: 6, textAlign: "center" }}>
                      <Typography
                        sx={{ fontSize: "14px", color: "#94a3b8" }}
                      >
                        No notifications yet
                      </Typography>
                    </Box>
                  ) : (
                    notifications.map((notification, index) => (
                      <Box
                        key={notification._id}
                        onClick={() => handleNotificationClick(notification._id)}
                        sx={{
                          display: "flex",
                          gap: 2,
                          px: 3,
                          py: 2.5,
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          animation: open
                            ? `${slideInItem} 0.3s ease forwards`
                            : "none",
                          animationDelay: `${index * 0.05}s`,
                          opacity: open ? 0 : 1,
                          backgroundColor: notification.read
                            ? "transparent"
                            : "rgba(99, 102, 241, 0.02)",
                          borderBottom:
                            index < notifications.length - 1
                              ? "1px solid #f8fafc"
                              : "none",
                          "&:hover": {
                            backgroundColor: "#f8fafc",
                          },
                        }}
                      >
                        {/* Status Dot */}
                        <Box
                          sx={{
                            flexShrink: 0,
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            backgroundColor: notification.read
                              ? "#d1d5db"
                              : getDotColor(notification.type),
                            mt: 0.8,
                            boxShadow: notification.read
                              ? "none"
                              : `0 0 0 3px ${getDotColor(notification.type)}20`,
                          }}
                        />

                        {/* Content */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: "15px",
                              fontWeight: notification.read ? 500 : 600,
                              color: notification.read ? "#64748b" : "#1e293b",
                              mb: 0.5,
                              lineHeight: 1.4,
                            }}
                          >
                            {notification.title}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              color: "#64748b",
                              lineHeight: 1.5,
                              mb: 1,
                            }}
                          >
                            {notification.description}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: "#94a3b8",
                              fontWeight: 500,
                            }}
                          >
                            {timeAgo(notification.createdAt)}
                          </Typography>
                        </Box>
                      </Box>
                    ))
                  )}
                </Box>

                {/* Footer */}
                <Divider sx={{ borderColor: "#f1f5f9" }} />
                <Box sx={{ p: 2 }}>
                  <Button
                    fullWidth
                    onClick={handleViewAll}
                    sx={{
                      textTransform: "none",
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#475569",
                      py: 1.2,
                      borderRadius: "10px",
                      backgroundColor: "#f8fafc",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: "#f1f5f9",
                        color: "#1e293b",
                      },
                    }}
                  >
                    View All Notifications
                  </Button>
                </Box>
              </Paper>
            </Fade>
          )}
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default NotificationDropdown;
